import React from 'react';
import { Box, Typography, Chip, Paper } from '@mui/material';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import FlagIcon from '@mui/icons-material/Flag';
import SendIcon from '@mui/icons-material/Send';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import GavelIcon from '@mui/icons-material/Gavel';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import EditIcon from '@mui/icons-material/Edit';
import { ReferralTimelineEvent } from '~/types/transition';

interface ReferralTimelineProps {
  events: ReferralTimelineEvent[];
}

type EventType = ReferralTimelineEvent['type'];

const EVENT_ICON: Record<EventType, React.ReactNode> = {
  RECEIVED: <MoveToInboxIcon fontSize="small" />,
  PROCESSING: <AutorenewIcon fontSize="small" />,
  FLAG: <FlagIcon fontSize="small" />,
  OUTREACH: <SendIcon fontSize="small" />,
  RESPONSE: <MarkEmailReadIcon fontSize="small" />,
  DECISION: <GavelIcon fontSize="small" />,
  EXECUTION: <RocketLaunchIcon fontSize="small" />,
  OVERRIDE: <EditIcon fontSize="small" />,
};

const EVENT_COLOR: Record<EventType, string> = {
  RECEIVED: '#1565c0',
  PROCESSING: '#1565c0',
  FLAG: '#e65100',
  OUTREACH: '#1565c0',
  RESPONSE: '#2e7d32',
  DECISION: '#6a1b9a',
  EXECUTION: '#2e7d32',
  OVERRIDE: '#e65100',
};

function formatTimestamp(isoString: string): string {
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

const ActorChip: React.FC<{ actor: string }> = ({ actor }) => {
  if (actor === 'SYSTEM') {
    return <Chip label="System" size="small" sx={{ fontSize: '0.65rem', height: 18 }} />;
  }
  if (actor === 'AI') {
    return <Chip label="AI" size="small" color="info" sx={{ fontSize: '0.65rem', height: 18 }} />;
  }
  return <Chip label={actor} size="small" color="success" sx={{ fontSize: '0.65rem', height: 18 }} />;
};

const ReferralTimeline: React.FC<ReferralTimelineProps> = ({ events }) => {
  const sorted = [...events].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>
        Timeline
      </Typography>
      <Box sx={{ position: 'relative', mt: 1 }}>
        {sorted.map((event, i) => {
          const color = EVENT_COLOR[event.type];
          const isLast = i === sorted.length - 1;

          return (
            <Box key={event.id} sx={{ display: 'flex', gap: 1.5, position: 'relative' }}>
              {/* Left: icon + connecting line */}
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, width: 28 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: color + '18',
                    border: `2px solid ${color}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color,
                    flexShrink: 0,
                    zIndex: 1,
                  }}
                >
                  {EVENT_ICON[event.type]}
                </Box>
                {!isLast && (
                  <Box sx={{ width: 2, flex: 1, backgroundColor: 'divider', minHeight: 16, my: 0.25 }} />
                )}
              </Box>

              {/* Right: content */}
              <Box sx={{ pb: isLast ? 0 : 2, flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.25 }}>
                  <ActorChip actor={event.actor} />
                  <Typography variant="caption" color="text.disabled">
                    {formatTimestamp(event.timestamp)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  {event.label}
                </Typography>
                {event.detail && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                    {event.detail}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default ReferralTimeline;
